import React, { useState } from "react";
import { EvtHeader, EvtSectionHeader } from "components/header";
import LoggingDisciplineCard from "./components/cards/logging-discipline";
import ApprovalRateCard from "./components/cards/approval-rate";
import UtilizationCard from "./components/cards/utilization";
import TaskEfficiencyCard from "./components/cards/task-efficiency";
import TeamLoggingDisciplineCard from "./components/cards/team-logging-discipline";
import TeamApprovalRateCard from "./components/cards/team-approval-rate";
import TeamUtilizationCard from "./components/cards/team-utilization";
import JobExtensionScoreCard from "./components/cards/job-completion";
import BusinessGenerationCard from "./components/cards/business-generation";
import BillingDelayCard from "./components/cards/billing-delay";
import { selectUser } from "store/src/slices/auth-slice";
import { useSelector } from "react-redux";
import PerformanceDetailsSidebar from "./components/performance-details-sidebar";
import JCSurveyCardSection from "./components/cards/jc-survey";
import AMSurveyCardSection from "./components/cards/am-survey";
import MarginAdherenceCard from "./components/cards/margin-adherence";
import PerformanceOverviewFilters from "./components/filters";

export default function PerformanceOverview() {
  const { role } = useSelector(selectUser);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Tag filter only applies to the Margin Adherence card.
  // Its query key includes tagId, so changing it refetches automatically.
  const [marginTagId, setMarginTagId] = useState(undefined);

  const isJobCoordinator = role?.is_job_coordinator;
  const isAccountManager = role?.is_account_manager;
  const isDepartmentHead = role?.is_department_head;
  const isTeamLead = role?.is_teamlead;
  const isProjectCoordinator = role?.is_project_coordinator;
  const isMilestoneCoordinator = role?.is_milestone_coordinator;

  const handleCardClick = (cardData) => {
    setSelectedCard(cardData);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedCard(null);
  };

  const shouldShowcaseFunctionalSection =
    isJobCoordinator ||
    isAccountManager ||
    isDepartmentHead ||
    isTeamLead ||
    isProjectCoordinator ||
    isMilestoneCoordinator;

  const shouldShowFinancialSection = isJobCoordinator || isAccountManager;

  return (
    <div className="w-full min-h-screen flex flex-col relative">
      {/* Sections Wrapper */}
      <div className="flex-1">
        {/* Core Section - Individual Contributor Cards */}
        <div>
          <EvtHeader title={`Core`} />
          <div className="flex gap-4 px-4 lg:px-6 flex-wrap">
            <LoggingDisciplineCard onClick={handleCardClick} />
            <UtilizationCard onClick={handleCardClick} />
            <ApprovalRateCard onClick={handleCardClick} />
            <TaskEfficiencyCard onClick={handleCardClick} />
          </div>
        </div>

        {shouldShowcaseFunctionalSection && (
          <>
            {/* Functional Section - Role-based Cards */}
            <div>
              <EvtHeader title={`Functional`} />
              <div className="flex gap-4 px-4 lg:px-6  flex-wrap">
                {/* Job Management Cards (Job Coordinator) */}
                {isJobCoordinator && <JobExtensionScoreCard onClick={handleCardClick} />}

                {/* Account Management Cards (Account Manager) */}
                {isAccountManager && (
                  <>
                    <BusinessGenerationCard onClick={handleCardClick} />
                    <BillingDelayCard onClick={handleCardClick} />
                  </>
                )}

                {/* Team Management Cards (Department Head) */}
                {isDepartmentHead && (
                  <>
                    <TeamLoggingDisciplineCard onClick={handleCardClick} />
                    <TeamApprovalRateCard onClick={handleCardClick} />
                    <TeamUtilizationCard onClick={handleCardClick} />
                  </>
                )}
              </div>
            </div>

            {/* Financial Section - tag filter applies only here */}
            {shouldShowFinancialSection && (
              <div>
                <EvtHeader title={`Financial`}>
                  <PerformanceOverviewFilters
                    tagId={marginTagId}
                    onChange={setMarginTagId}
                    usePortal={false}
                  />
                </EvtHeader>
                <div className="flex gap-4 px-4 lg:px-6 flex-wrap">
                  <MarginAdherenceCard tagId={marginTagId} onClick={handleCardClick} />
                </div>
              </div>
            )}

            <div>
              {isJobCoordinator && <JCSurveyCardSection onClick={handleCardClick} />}
              {isAccountManager && <AMSurveyCardSection onClick={handleCardClick} />}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 w-full bg-muted py-3 text-center text-sm text-muted-foreground z-10 mt-8 shadow-sm">
        Provides an overview of performance from the past 12 months.
      </div>

      {/* Performance Details Sidebar */}
      <PerformanceDetailsSidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        cardData={selectedCard}
      />
    </div>
  );
}
